import type { Checkpoint, World } from '../../shared/types.js';
import { num } from '../i18n/i18n.js';

/**
 * The minimap: a rotating, player-centred street map in the corner of the screen.
 *
 * It works the way a car navigation screen does rather than the way a paper map does. The
 * map turns under a fixed car so that "up" is always where you are pointing, which is the
 * whole reason the thing is glanceable at 90 km/h: a left turn on the map is a left turn on
 * the wheel, with no rotation to do in your head. North-up is still available — press the
 * compass — because it is the better map for working out where you are in the city, just
 * not for deciding what to do in the next two seconds.
 *
 * The street layer is rasterised once per world into an offscreen canvas at a fixed
 * metres-per-pixel, and every frame blits that image through a rotate/scale transform. Three
 * thousand roads are drawn once; each frame after that is one `drawImage` plus the dozen
 * moving markers, which is what makes a map that zooms and rotates cost the same as the flat
 * one it replaced.
 */

export interface MinimapPlayer {
  x: number;
  z: number;
  /** Radians, game convention: forward is (-sin h, -cos h). */
  heading: number;
  color: number;
  self: boolean;
  /** Shown beside the other cars, so the arrow on the map is somebody. */
  name?: string;
}

export type MinimapMode = 'follow' | 'overview';

/** Metres across the map, closest first. */
const ZOOM_LEVELS = [130, 220, 380];
const DEFAULT_ZOOM = 1;

/**
 * The pre-rendered street layer, in device pixels along one side.
 *
 * Sized for the closest zoom rather than for the map element: at 220 m across a 260 px map
 * the display works out to a bit over 1 px/m on a 2× screen, so a base any coarser than
 * this would be visibly soft exactly when you are looking at it hardest.
 */
const BASE_MAX_PX = 1800;
const BASE_MAX_PX_PER_M = 1.8;
const BASE_MIN_PX_PER_M = 0.4;

const ROUTE_COLOR = '#ffd84d';
const NEXT_COLOR = '#ffd84d';
const PENDING_COLOR = '#39c6ff';
const PASSED_COLOR = '#4a5a52';

// ---------------------------------------------------------------------------
// the parts worth testing without a canvas
// ---------------------------------------------------------------------------

export interface ZoomState {
  mode: MinimapMode;
  /** Index into `ZOOM_LEVELS`; ignored while `mode` is `overview`. */
  index: number;
}

/** Positive steps zoom in. The overview is the level beyond the widest follow level. */
export function zoomStep(state: ZoomState, step: number): ZoomState {
  if (step > 0) {
    if (state.mode === 'overview') {
      return { mode: 'follow', index: ZOOM_LEVELS.length - 1 };
    }
    return { mode: 'follow', index: Math.max(0, state.index - 1) };
  }
  if (state.mode === 'overview') return state;
  if (state.index >= ZOOM_LEVELS.length - 1) return { mode: 'overview', index: state.index };
  return { mode: 'follow', index: state.index + 1 };
}

export interface MapCamera {
  x: number;
  z: number;
  /** Rotation applied to the whole map, in radians. Zero is north-up. */
  angle: number;
  pxPerM: number;
}

/**
 * World metres to pixels on the map.
 *
 * World +x runs right and world +z runs down, which is the screen's own convention, so the
 * only real content here is the rotation. In follow mode `angle` is the car's heading, and
 * because forward is `(-sin h, -cos h)` that is exactly the rotation that puts the car's
 * nose at the top of the map — see `tests/minimap.test.ts`, which pins the sign down.
 */
export function screenPoint(
  x: number,
  z: number,
  camera: MapCamera,
  centre: number,
): [number, number] {
  const dx = (x - camera.x) * camera.pxPerM;
  const dz = (z - camera.z) * camera.pxPerM;
  const cos = Math.cos(camera.angle);
  const sin = Math.sin(camera.angle);
  return [centre + dx * cos - dz * sin, centre + dx * sin + dz * cos];
}

/** How much of the world one map fits, in metres across, for the given state. */
export function metresAcross(state: ZoomState, worldSpan: number): number {
  return state.mode === 'overview' ? worldSpan : ZOOM_LEVELS[state.index];
}

export class Minimap {
  private readonly ctx: CanvasRenderingContext2D;
  private base: HTMLCanvasElement | null = null;
  private world: World | null = null;
  private observer: ResizeObserver | null = null;

  /** Device pixels along one side of the visible map. */
  private size = 200;
  private dpr = 1;
  private basePxPerM = 1;
  private baseSize = 0;

  private zoom: ZoomState = { mode: 'follow', index: DEFAULT_ZOOM };
  /** The route, its gates and the pointer to the next one. Off in a free drive. */
  private showRoute = true;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Minimap needs a 2D canvas context');
    this.ctx = ctx;
    // The map is sized in CSS (it scales with the viewport), so its pixel buffer has to
    // follow. Reading `clientWidth` in the draw call instead would force a layout on every
    // frame of the render loop.
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(canvas);
    }
    this.measure();
  }

  setWorld(world: World): void {
    this.world = world;
    this.rasterise();
  }

  /** Off for a free drive: a route nobody is following is a line across the city's map. */
  setRouteVisible(visible: boolean): void {
    if (visible === this.showRoute) return;
    this.showRoute = visible;
    if (this.world) this.rasterise();
  }

  get currentMode(): MinimapMode {
    return this.zoom.mode;
  }

  setMode(mode: MinimapMode): void {
    this.zoom = { mode, index: mode === 'follow' ? DEFAULT_ZOOM : this.zoom.index };
  }

  toggleMode(): MinimapMode {
    this.setMode(this.zoom.mode === 'follow' ? 'overview' : 'follow');
    return this.zoom.mode;
  }

  /**
   * Steps the zoom, with the whole-region overview as the level past the widest one — so
   * the two buttons drive a single continuous range rather than a range plus a mode.
   */
  zoomBy(step: number): void {
    this.zoom = zoomStep(this.zoom, step);
  }

  /** True when there is room to move in that direction — drives the +/− buttons. */
  canZoom(step: number): boolean {
    const next = zoomStep(this.zoom, step);
    return next.mode !== this.zoom.mode || next.index !== this.zoom.index;
  }

  private measure(): void {
    this.dpr = Math.min(window.devicePixelRatio ?? 1, 2);
    const css = this.canvas.clientWidth || 200;
    const next = Math.round(css * this.dpr);
    if (next === this.size && this.canvas.width === next) return;
    this.size = next;
    this.canvas.width = next;
    this.canvas.height = next;
  }

  // -------------------------------------------------------------------------
  // the street layer
  // -------------------------------------------------------------------------

  private rasterise(): void {
    const world = this.world;
    if (!world) return;

    const span = Math.max(world.extent.x, world.extent.z) * 2 + 60;
    this.basePxPerM = Math.min(
      BASE_MAX_PX_PER_M,
      Math.max(BASE_MIN_PX_PER_M, BASE_MAX_PX / span),
    );
    this.baseSize = Math.ceil(span * this.basePxPerM);

    const base = document.createElement('canvas');
    base.width = this.baseSize;
    base.height = this.baseSize;
    const ctx = base.getContext('2d')!;
    const px = (metres: number) => this.baseSize / 2 + metres * this.basePxPerM;

    ctx.fillStyle = '#10161a';
    ctx.fillRect(0, 0, this.baseSize, this.baseSize);

    for (const area of world.areas) {
      if (area.kind === 'urban') continue;
      ctx.fillStyle =
        area.kind === 'water'
          ? '#12354f'
          : area.kind === 'paved' || area.kind === 'parking'
            ? '#20211f'
            : '#17301a';
      ctx.beginPath();
      trace(ctx, area.poly, px, true);
      ctx.fill();
    }

    // Buildings as a faint wash rather than outlines. Individually they are illegible at
    // this scale; collectively they are what makes a block look like a block.
    ctx.fillStyle = 'rgba(120, 134, 150, 0.16)';
    for (const building of world.buildings) {
      ctx.beginPath();
      trace(ctx, building.poly, px, true);
      ctx.fill();
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Casing first, then the fill, so junctions read as junctions instead of a grey mesh.
    for (const pass of [0, 1]) {
      for (const road of world.roads) {
        const major = road.width >= 11;
        ctx.strokeStyle = pass === 0 ? '#080b0d' : major ? '#79808d' : '#4d545e';
        const core = Math.max(major ? 2.4 : 1.6, road.width * this.basePxPerM * 0.8);
        ctx.lineWidth = pass === 0 ? core + 1.6 : core;
        ctx.beginPath();
        trace(ctx, road.pts, px, false);
        ctx.stroke();
      }
    }

    // The racing line, dark-cased so it survives being drawn over a pale main road.
    if (this.showRoute) {
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
      ctx.lineWidth = 8;
      ctx.beginPath();
      trace(ctx, world.route.path, px, false);
      ctx.stroke();
      ctx.strokeStyle = ROUTE_COLOR;
      ctx.lineWidth = 4.5;
      ctx.beginPath();
      trace(ctx, world.route.path, px, false);
      ctx.stroke();
    }

    this.base = base;
  }

  // -------------------------------------------------------------------------
  // per frame
  // -------------------------------------------------------------------------

  /**
   * @param players every car on the map; exactly one of them flagged `self`
   * @param nextCheckpoint index of the gate the local player is heading for
   */
  draw(players: MinimapPlayer[], nextCheckpoint: number): void {
    const world = this.world;
    const base = this.base;
    if (!world || !base) return;

    const self = players.find((p) => p.self);
    const ctx = this.ctx;
    const size = this.size;
    const centre = size / 2;
    // A follow map with nobody to follow is a map centred on nothing; fall back to the
    // overview until the local car exists.
    const state: ZoomState = self ? this.zoom : { mode: 'overview', index: this.zoom.index };
    const worldSpan = Math.max(world.extent.x, world.extent.z) * 2 + 60;

    const camera: MapCamera = {
      x: state.mode === 'follow' ? self!.x : 0,
      z: state.mode === 'follow' ? self!.z : 0,
      angle: state.mode === 'follow' ? self!.heading : 0,
      pxPerM: size / metresAcross(state, worldSpan),
    };
    const project = (x: number, z: number) => screenPoint(x, z, camera, centre);
    const angle = camera.angle;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, size, size);

    // The street layer, through the same transform `screenPoint` applies by hand.
    ctx.save();
    ctx.translate(centre, centre);
    ctx.rotate(angle);
    const k = camera.pxPerM / this.basePxPerM;
    ctx.scale(k, k);
    ctx.translate(
      -(this.baseSize / 2 + camera.x * this.basePxPerM),
      -(this.baseSize / 2 + camera.z * this.basePxPerM),
    );
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(base, 0, 0);
    ctx.restore();

    const s = this.dpr;
    const route = world.route;
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 260);

    // --- gates --------------------------------------------------------------
    // Numbered, because "the third one" is how people talk about them and because a ring
    // with no number is indistinguishable from every other ring once there are five on
    // screen. Passed gates stay, dimmed — knowing where you have been is how you notice you
    // are going back on yourself.
    const gates = this.showRoute ? route.checkpoints.length : 0;
    for (let i = 0; i < gates; i++) {
      const cp = route.checkpoints[i];
      const [x, y] = project(cp.x, cp.z);
      if (offCanvas(x, y, size, 40 * s)) continue;
      const passed = i < nextCheckpoint;
      const next = i === nextCheckpoint;
      const r = (next ? 8.5 : passed ? 3.5 : 6) * s;

      if (next) {
        ctx.beginPath();
        ctx.arc(x, y, r + (3 + pulse * 5) * s, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 216, 77, ${0.55 - pulse * 0.4})`;
        ctx.lineWidth = 2 * s;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      if (next) {
        ctx.fillStyle = NEXT_COLOR;
        ctx.fill();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.lineWidth = 2 * s;
        ctx.stroke();
      } else {
        ctx.fillStyle = 'rgba(6, 10, 12, 0.75)';
        ctx.fill();
        ctx.strokeStyle = passed ? PASSED_COLOR : PENDING_COLOR;
        ctx.lineWidth = 2 * s;
        ctx.stroke();
      }

      if (!passed) {
        label(ctx, String(i + 1), x, y, (next ? 10 : 8) * s, next ? '#12160f' : PENDING_COLOR);
      }
    }

    // --- finish -------------------------------------------------------------
    const [fx, fy] = project(route.finish.x, route.finish.z);
    if (this.showRoute && !offCanvas(fx, fy, size, 40 * s)) {
      drawFlag(ctx, fx, fy, 7 * s, nextCheckpoint >= route.checkpoints.length ? 1 + pulse * 0.35 : 1);
    }

    // --- the cars -----------------------------------------------------------
    // Yours last so it is never buried under someone else's on a shared grid slot. The others
    // are named, and one that is off the map gets an arrow on the rim in its colour — the
    // only way to find somebody in a free drive, and worth having in a race too.
    for (const player of players) {
      if (player.self) continue;
      const [x, y] = project(player.x, player.z);
      const fill = hexOf(player.color);
      if (offCanvas(x, y, size, 4 * s)) {
        const dx = x - centre;
        const dy = y - centre;
        const dist = Math.hypot(dx, dy);
        if (dist < 1) continue;
        const rim = centre - 10 * s;
        drawChevron(ctx, centre + (dx / dist) * rim, centre + (dy / dist) * rim, Math.atan2(dy, dx), 7 * s, fill);
        continue;
      }
      // In a free drive there is no gate to aim for; the other cars are the beacons.
      if (!this.showRoute) {
        ctx.beginPath();
        ctx.arc(x, y, (9 + pulse * 6) * s, 0, Math.PI * 2);
        ctx.strokeStyle = fill;
        ctx.globalAlpha = 0.65 - pulse * 0.45;
        ctx.lineWidth = 2 * s;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      drawCar(ctx, x, y, angle - player.heading, 7.5 * s, fill, 'rgba(0,0,0,0.85)');
      if (player.name) nameTag(ctx, player.name, x, y - 11 * s, 9 * s);
    }
    if (self) {
      const [x, y] = project(self.x, self.z);
      drawCar(ctx, x, y, angle - self.heading, 8.5 * s, hexOf(self.color), '#ffffff');
    }

    // --- the pointer to the next gate ---------------------------------------
    // Zoomed in far enough to be useful, the thing you need is usually off the map. A
    // chevron pinned to the rim with the distance next to it is the one readout that turns
    // "I can see three streets" back into "it is 240 m that way".
    const target: Checkpoint =
      nextCheckpoint < route.checkpoints.length ? route.checkpoints[nextCheckpoint] : route.finish;
    if (self && this.showRoute) {
      const [tx, ty] = project(target.x, target.z);
      const dx = tx - centre;
      const dy = ty - centre;
      const dist = Math.hypot(dx, dy);
      const rim = centre - 13 * s;
      if (dist > rim) {
        const px = centre + (dx / dist) * rim;
        const py = centre + (dy / dist) * rim;
        drawChevron(ctx, px, py, Math.atan2(dy, dx), 9 * s);
        const metres = Math.hypot(target.x - self.x, target.z - self.z);
        const text = metres >= 1000 ? `${num(metres / 1000, 1)} km` : `${Math.round(metres)} m`;
        pill(ctx, text, centre + (dx / dist) * (rim - 22 * s), centre + (dy / dist) * (rim - 22 * s), 9 * s);
      }
    }

    // --- compass ------------------------------------------------------------
    // North lives at the map's rotation, so in follow mode the needle is the only thing
    // telling you the map turned at all.
    const cr = 13 * s;
    const cx = size - cr - 7 * s;
    const cy = cr + 7 * s;
    ctx.beginPath();
    ctx.arc(cx, cy, cr, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(6, 10, 12, 0.72)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.22)';
    ctx.lineWidth = 1 * s;
    ctx.stroke();
    // North is -Z in world space; the same rotation the map got.
    const nx = cx + Math.sin(angle) * cr * 0.62;
    const ny = cy - Math.cos(angle) * cr * 0.62;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(nx, ny);
    ctx.strokeStyle = '#ff5a4d';
    ctx.lineWidth = 2 * s;
    ctx.stroke();
    label(ctx, 'N', nx, ny, 8 * s, '#ff8a80');
  }

  dispose(): void {
    this.observer?.disconnect();
    this.observer = null;
  }
}

// ---------------------------------------------------------------------------
// drawing helpers
// ---------------------------------------------------------------------------

function trace(
  ctx: CanvasRenderingContext2D,
  pts: number[],
  px: (m: number) => number,
  close: boolean,
): void {
  for (let i = 0; i < pts.length; i += 2) {
    const x = px(pts[i]);
    const y = px(pts[i + 1]);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  if (close) ctx.closePath();
}

function offCanvas(x: number, y: number, size: number, margin: number): boolean {
  return x < -margin || y < -margin || x > size + margin || y > size + margin;
}

function hexOf(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

/**
 * A car as a rounded arrowhead rather than a dot. The shape carries which way it is
 * pointing, which is the difference between "someone is at that junction" and "someone is
 * coming at you".
 */
function drawCar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  heading: number,
  r: number,
  fill: string,
  stroke: string,
): void {
  ctx.save();
  ctx.translate(x, y);
  // `heading` here is already screen-relative: the caller passes (map rotation − world
  // heading), which is what turns a car pointing north into an arrow pointing up when the
  // map is north-up, and into nothing at all when the map has turned with you.
  ctx.rotate(heading);
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(r * 0.72, r * 0.78);
  ctx.lineTo(0, r * 0.4);
  ctx.lineTo(-r * 0.72, r * 0.78);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = Math.max(1, r * 0.22);
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.restore();
}

function drawChevron(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  r: number,
  fill: string = NEXT_COLOR,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(-r * 0.7, -r * 0.8);
  ctx.lineTo(-r * 0.25, 0);
  ctx.lineTo(-r * 0.7, r * 0.8);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.8)';
  ctx.lineWidth = Math.max(1, r * 0.2);
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.restore();
}

function drawFlag(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, glow: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.arc(0, 0, r * glow, 0, Math.PI * 2);
  ctx.fillStyle = '#f4f6f8';
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.lineWidth = Math.max(1, r * 0.28);
  ctx.stroke();
  // Two dark quadrants: a chequered flag at eight pixels across.
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, r * 0.98, 0, Math.PI / 2);
  ctx.closePath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, r * 0.98, Math.PI, Math.PI * 1.5);
  ctx.closePath();
  ctx.fillStyle = '#12161a';
  ctx.fill();
  ctx.restore();
}

function label(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fontPx: number,
  color: string,
): void {
  ctx.font = `700 ${fontPx}px 'Roboto Flex', system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

/** A name above a car: white on a dark outline, so it reads over a pale main road too. */
function nameTag(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, fontPx: number): void {
  ctx.font = `600 ${fontPx}px 'Roboto Flex', system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = fontPx * 0.32;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(6, 10, 12, 0.9)';
  ctx.strokeText(text, x, y);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, x, y);
}

function pill(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fontPx: number,
): void {
  ctx.font = `700 ${fontPx}px 'Roboto Flex', system-ui, sans-serif`;
  const w = ctx.measureText(text).width + fontPx * 0.9;
  const h = fontPx * 1.7;
  ctx.beginPath();
  ctx.roundRect(x - w / 2, y - h / 2, w, h, h / 2);
  ctx.fillStyle = 'rgba(6, 10, 12, 0.82)';
  ctx.fill();
  label(ctx, text, x, y, fontPx, '#ffd84d');
}
